import { buildTaxId } from "./taxIds";

// The billing profile — who invoices are made out to — between the API's shape
// and the form's.
//
// The form names its fields by the API's own paths (["address", "city"]), so the
// `field` a validation failure names ("address.city") is also the name of the
// form field to put the message under, with nothing to translate in between.
//
// The form shows only the address. Name and email ride along unseen and no tax
// ID is taken, so a message about any of them has no field to go under.

const PROFILE_FIELDS = [
   ["address", "line1"],
   ["address", "line2"],
   ["address", "city"],
   ["address", "state"],
   ["address", "postalCode"],
   ["address", "country"]
];

const KNOWN_FIELDS = new Set(PROFILE_FIELDS.map((path) => path.join(".")));

// Paths the server may name that sit on a field under another name.
const FIELD_ALIASES = {
   address: ["address", "line1"]
};

/**
 * The form field a server `field` path is about, or null when the form has no
 * such field (the message is then shown for the form as a whole). Read in either
 * casing, since the envelope is camelCase and the model state it comes from is
 * not always.
 */
export const fieldPathToName = (field) => {
   if (typeof field !== "string" || !field.trim()) return null;

   const path = field
      .trim()
      .split(".")
      .map((part) => part.charAt(0).toLowerCase() + part.slice(1))
      .join(".");

   if (FIELD_ALIASES[path]) return FIELD_ALIASES[path];
   return KNOWN_FIELDS.has(path) ? path.split(".") : null;
};

/** A profile (or none yet) as the form's values, with `defaults` filling what it lacks. */
export const toProfileFormValues = (profile, defaults = {}) => ({
   name: profile?.name || defaults.name || "",
   email: profile?.email || defaults.email || "",
   address: {
      line1: profile?.address?.line1 || "",
      line2: profile?.address?.line2 || "",
      city: profile?.address?.city || "",
      state: profile?.address?.state || "",
      postalCode: profile?.address?.postalCode || "",
      country: profile?.address?.country?.toUpperCase() || defaults.country || undefined
   }
});

const trimmed = (value) => (typeof value === "string" ? value.trim() : "");
const optional = (value) => trimmed(value) || null;

/**
 * The form's values as the body of PUT /Payment/billing-profile. The read-only
 * fields (`isComplete`, `taxIdVerificationStatus`) are the server's to report
 * and are not sent. `saved` is the profile as last returned, which the tax ID
 * type is worked out against (taxIds.js).
 */
export const toProfilePayload = (values, organizationId, saved) => {
   const country = trimmed(values?.address?.country).toUpperCase();

   return {
      organizationId,
      name: trimmed(values?.name),
      email: trimmed(values?.email),
      address: {
         line1: trimmed(values?.address?.line1),
         line2: optional(values?.address?.line2),
         city: trimmed(values?.address?.city),
         state: optional(values?.address?.state),
         postalCode: trimmed(values?.address?.postalCode),
         country
      },
      taxId: buildTaxId(country, values?.taxId?.value, saved)
   };
};

/** The address as one line, for a summary. */
export const formatProfileAddress = (profile, countryName = (code) => code) => {
   const address = profile?.address;
   if (!address) return "";

   return [
      address.line1,
      address.line2,
      [address.postalCode, address.city].filter(Boolean).join(" "),
      address.state,
      address.country ? countryName(address.country) : null
   ]
      .filter(Boolean)
      .join(", ");
};
